import {
  CheckCircleIcon,
  CircleIcon,
  WarningCircle,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button, Input, PasswordInput } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { getPasswordRequirements } from "@/packages/features-auth";

export interface SignUpFormProps {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  password: string;
  onPasswordChange: (value: string) => void;
  isLoading?: boolean;
  error?: string | null;
  hasAttemptedSubmit?: boolean;
  onToggleFlow: () => void;
}

const stagger = {
  hidden: { opacity: 0, y: 10 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  }),
};

export function SignUpForm({
  onSubmit,
  password,
  onPasswordChange,
  isLoading = false,
  error,
  hasAttemptedSubmit = false,
  onToggleFlow,
}: SignUpFormProps) {
  const { t } = useTranslation(["auth"]);
  const requirements = getPasswordRequirements(password, {
    minLength: t("auth:passwordRequirements.minLength"),
    uppercase: t("auth:passwordRequirements.uppercase"),
    lowercase: t("auth:passwordRequirements.lowercase"),
    number: t("auth:passwordRequirements.number"),
  });
  const showRequirements = password.length > 0 || hasAttemptedSubmit;

  return (
    <div className="flex flex-col">
      {/* Header */}
      <motion.div custom={0} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          {t("auth:signUp.title")}
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          {t("auth:signUp.subtitle")}
        </p>
      </motion.div>

      {/* Form */}
      <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-4">
        {/* Name */}
        <motion.div
          custom={1}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="name"
            className="text-[13px] font-medium text-secondary"
          >
            {t("auth:signUp.nameLabel")}
          </label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder={t("auth:signUp.namePlaceholder")}
            autoComplete="name"
          />
        </motion.div>

        {/* Email */}
        <motion.div
          custom={2}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="email"
            className="text-[13px] font-medium text-secondary"
          >
            {t("auth:signUp.emailLabel")}
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder={t("auth:signUp.emailPlaceholder")}
            required
            autoComplete="email"
          />
        </motion.div>

        {/* Password */}
        <motion.div
          custom={3}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="password"
            className="text-[13px] font-medium text-secondary"
          >
            {t("auth:signUp.passwordLabel")}
          </label>
          <PasswordInput
            id="password"
            name="password"
            placeholder={t("auth:signUp.passwordPlaceholder")}
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            showLabel={t("auth:password.show")}
            hideLabel={t("auth:password.hide")}
          />

          {/* Password requirements */}
          {showRequirements && (
            <motion.ul
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.25 }}
              className="mt-1.5 flex flex-col gap-1"
            >
              {requirements.map((req) => (
                <li
                  key={req.label}
                  className="flex items-center gap-1.5 text-[12px]"
                >
                  {req.isValid ? (
                    <CheckCircleIcon
                      className="size-3.5 text-accent"
                      weight="fill"
                    />
                  ) : (
                    <CircleIcon className="size-3.5 text-muted" />
                  )}
                  <span
                    className={`transition-colors duration-200 ${
                      req.isValid ? "text-accent" : "text-muted"
                    }`}
                  >
                    {req.label}
                  </span>
                </li>
              ))}
            </motion.ul>
          )}
        </motion.div>

        {/* Confirm Password */}
        <motion.div
          custom={4}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="confirm-password"
            className="text-[13px] font-medium text-secondary"
          >
            {t("auth:signUp.confirmPasswordLabel")}
          </label>
          <PasswordInput
            id="confirm-password"
            name="confirm-password"
            placeholder={t("auth:signUp.confirmPasswordPlaceholder")}
            required
            autoComplete="new-password"
            showLabel={t("auth:password.show")}
            hideLabel={t("auth:password.hide")}
          />
        </motion.div>

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2.5"
          >
            <WarningCircle
              className="mt-0.5 size-4 shrink-0 text-red-500"
              weight="fill"
            />
            <p className="text-[13px] text-red-600">{error}</p>
          </motion.div>
        )}

        {/* Submit */}
        <motion.div
          custom={5}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            className="mt-1 w-full"
          >
            {t("auth:signUp.submit")}
          </Button>
        </motion.div>

        {/* Toggle */}
        <motion.p
          custom={6}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="text-center text-[13px] text-secondary"
        >
          {t("auth:signUp.hasAccount")}{" "}
          <button
            type="button"
            onClick={onToggleFlow}
            className="font-medium text-primary hover:text-accent transition-colors"
          >
            {t("auth:signUp.signInLink")}
          </button>
        </motion.p>
      </form>

      {/* Legal disclaimer */}
      <motion.p
        custom={7}
        variants={stagger}
        initial="hidden"
        animate="show"
        className="mt-6 text-center text-[11px] leading-relaxed text-muted"
      >
        {t("auth:signUp.legalPrefix")}{" "}
        <a
          href={t("auth:signUp.termsUrl")}
          target="_blank"
          rel="noopener noreferrer"
          className="text-secondary underline underline-offset-2 hover:text-primary transition-colors"
        >
          {t("auth:signUp.termsOfService")}
        </a>
        {", "}
        {t("auth:signUp.legalThe")}{" "}
        <a
          href={t("auth:signUp.dpaUrl")}
          target="_blank"
          rel="noopener noreferrer"
          className="text-secondary underline underline-offset-2 hover:text-primary transition-colors"
        >
          {t("auth:signUp.dpa")}
        </a>{" "}
        {t("auth:signUp.legalAnd")} {t("auth:signUp.legalThe")}{" "}
        <a
          href={t("auth:signUp.privacyUrl")}
          target="_blank"
          rel="noopener noreferrer"
          className="text-secondary underline underline-offset-2 hover:text-primary transition-colors"
        >
          {t("auth:signUp.privacyPolicy")}
        </a>
        .
      </motion.p>
    </div>
  );
}
